import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { FiMail, FiPhone, FiMapPin, FiCheckCircle } from 'react-icons/fi'
import api from '../../utils/api'
import { useConsent } from '../../context/ConsentContext'
import Wordmark from '../ui/Wordmark'
import { isNativeApp } from '../../utils/platform'

// Store badges for the CONSUMER app (com.mariusnpop.petzy) — this footer is the
// pet-owner site. Petzy Business belongs on /pro, not here.
//
// The artwork is Apple's and Google's, used unmodified: both brands forbid
// recolouring, re-typesetting or distorting the badge, so these are plain <img>
// at one height, and each width falls out of its own aspect ratio (Apple 2.99:1,
// Google 3.37:1 — they are not meant to match). White Apple lockup because the
// footer is bg-ink; Google's badge carries its own #a6a6a6 keyline, so it reads
// on dark too.
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.mariusnpop.petzy'
const APP_STORE_URL = 'https://apps.apple.com/ro/app/id6788333275'

// Both badges render. Kept as a constant rather than deleted because the reason
// it existed is worth remembering: approval is NOT availability. The app sat at
// "1.0.1 Ready for Distribution" while App Store Connect reported it available in
// zero of 175 countries — 174 "Not Available" plus Romania, the base country,
// "Cannot Sell" — so the store URL 404'd and this badge would have been dead.
// What freed it was the Digital Services Act trader status: once that went Active
// on the account, Romania flipped to Available and apps.apple.com started serving
// (verified: 200, title "Petzy App - App Store"). Apple's own index lags behind
// that by hours, so a lookup returning nothing proves nothing.
// If a future release is ever pulled from sale, set this back to false rather
// than shipping a badge to a 404.
const IOS_APP_AVAILABLE = true

function StoreBadges() {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Descarcă aplicația Petzy din Google Play"
      >
        <img src="/badge-google-play-ro.svg" alt="Disponibil și pe Google Play" className="h-10 w-auto" />
      </a>
      {IOS_APP_AVAILABLE && (
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Descarcă aplicația Petzy din App Store"
        >
          <img src="/badge-app-store-ro.svg" alt="Descarcă din App Store" className="h-10 w-auto" />
        </a>
      )}
    </div>
  )
}

export default function Footer() {
  // Pulled lazily so non-Layout pages that don't render the footer
  // also don't pay the context lookup cost on every render.
  const { openSettings } = useConsent();
  const [company, setCompany] = useState({});
  // Contact info pulled from /content/contact (admin-editable settings).
  // Falsy fields render as no row instead of as a hardcoded placeholder
  // so the footer never advertises a phone number or email we don't own.
  const [contact, setContact] = useState({});

  // Newsletter form state — mirrors NewsletterPopup (same payload, same
  // endpoint) but rendered inline as a persistent subscribe affordance.
  const [nlName, setNlName] = useState('');
  const [nlEmail, setNlEmail] = useState('');
  const [nlSubmitting, setNlSubmitting] = useState(false);
  const [nlSuccess, setNlSuccess] = useState(false);

  useEffect(() => {
    api.get('/content/company')
      .then(({ data }) => setCompany(data.company || {}))
      .catch(() => {});
    api.get('/content/contact')
      .then(({ data }) => setContact(data.contact || {}))
      .catch(() => {});
  }, []);

  // Address line collapses city + street so an admin who only fills
  // one (e.g. just "Bucuresti") still gets a clean single-line render.
  const addressLine = [contact.address, contact.city].filter(Boolean).join(', ');
  const hasAnyContact = Boolean(contact.email || contact.phone || addressLine);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = nlName.trim();
    const trimmedEmail = nlEmail.trim();
    if (trimmedName.length < 2) {
      toast.error('Te rugam sa completezi numele.');
      return;
    }
    setNlSubmitting(true);
    try {
      await api.post('/platform/subscribe', {
        name: trimmedName,
        email: trimmedEmail,
        // Backend coerces anything outside WAITLIST/BOOKING to
        // PLATFORM_POPUP, so we just send the popup source — both entry
        // points end up as platform subscribers in the same audience.
        source: 'PLATFORM_POPUP',
      });
      toast.success('Te-ai inscris cu succes la newsletter!');
      setNlSuccess(true);
      setNlName('');
      setNlEmail('');
      // Keep the success state visible for a few seconds, then restore
      // the form so a second user on the same device can subscribe.
      setTimeout(() => setNlSuccess(false), 6000);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Nu am putut procesa abonarea. Incearca din nou.';
      toast.error(msg);
    } finally {
      setNlSubmitting(false);
    }
  };

  // Native (B2C app): no footer at all — the Bottom Tab Bar is the nav, and
  // legal/support live in Profil → Setări (an iOS-style settings list). A
  // footer here would read as repackaged-website chrome (App Store 4.2).
  if (isNativeApp) return null;

  return (
    <footer className="bg-ink text-cream-50/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Newsletter (static subscribe) — full width above the column grid
            so it's the first thing users see in the footer. Shares the
            /platform/subscribe endpoint with NewsletterPopup. */}
        <div className="mb-12 pb-10 border-b border-cream-50/15">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="font-display text-xl sm:text-2xl font-bold text-cream-50 mb-1">Fii la curent</h3>
              <p className="text-sm text-cream-50/60 leading-relaxed">
                Reduceri, saloane noi si sfaturi pentru animalul tau — direct pe email.
              </p>
            </div>
            {nlSuccess ? (
              <div className="flex items-center gap-3 bg-emerald-900/30 border border-emerald-700/40 rounded-xl px-4 py-3 text-emerald-200">
                <FiCheckCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">
                  Te-ai inscris cu succes! Iti multumim — te vom anunta cand apar noutati.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleNewsletterSubmit}
                className="flex flex-col sm:flex-row gap-2"
                aria-label="Abonare la newsletter"
              >
                <label className="sr-only" htmlFor="footer-nl-name">Nume</label>
                <input
                  id="footer-nl-name"
                  type="text"
                  value={nlName}
                  onChange={(e) => setNlName(e.target.value)}
                  placeholder="Nume"
                  required
                  minLength={2}
                  maxLength={120}
                  disabled={nlSubmitting}
                  className="flex-1 min-w-0 px-4 py-2.5 bg-cream-50/5 border border-cream-50/15 rounded-xl text-sm text-cream-50 placeholder-cream-50/60 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 disabled:opacity-60"
                />
                <label className="sr-only" htmlFor="footer-nl-email">Email</label>
                <input
                  id="footer-nl-email"
                  type="email"
                  value={nlEmail}
                  onChange={(e) => setNlEmail(e.target.value)}
                  placeholder="adresa@email.ro"
                  required
                  maxLength={180}
                  disabled={nlSubmitting}
                  className="flex-1 min-w-0 px-4 py-2.5 bg-cream-50/5 border border-cream-50/15 rounded-xl text-sm text-cream-50 placeholder-cream-50/60 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={nlSubmitting}
                  className="px-5 py-2.5 bg-primary-700 text-white rounded-xl text-sm font-semibold hover:bg-primary-800 transition disabled:opacity-60 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-cream-50"
                >
                  {nlSubmitting ? 'Se trimite...' : 'Aboneaza-te'}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* About */}
          <div>
            <div className="mb-4">
              <Wordmark onInk className="text-2xl" />
            </div>
            <p className="text-cream-50/70 text-sm leading-relaxed mb-6">
              Platforma ta de programări la saloanele de toaletaj pentru animale de companie. Găsește, compară și programează-te rapid la cele mai bune saloane de grooming din orașul tău.
            </p>
            <StoreBadges />
          </div>

          {/* Links */}
          <div>
            <h3 className="font-display text-cream-50 font-semibold mb-4">Linkuri rapide</h3>
            <ul className="space-y-3">
              <li><Link to="/search" className="text-sm hover:text-cream-50 transition-colors">Caută saloane</Link></li>
              <li><Link to="/map" className="text-sm hover:text-cream-50 transition-colors">Hartă saloane</Link></li>
              <li><Link to="/about" className="text-sm hover:text-cream-50 transition-colors">Despre noi</Link></li>
              <li><Link to="/blog" className="text-sm hover:text-cream-50 transition-colors">Blog</Link></li>
              <li><Link to="/pro/register" className="text-sm hover:text-cream-50 transition-colors">Înregistrează-ți salonul</Link></li>
              <li><Link to="/faq" className="text-sm hover:text-cream-50 transition-colors">Intrebari frecvente</Link></li>
              <li><Link to="/contact" className="text-sm hover:text-cream-50 transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-display text-cream-50 font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              <li><Link to="/terms" className="text-sm hover:text-cream-50 transition-colors">Termeni si conditii</Link></li>
              <li><Link to="/privacy" className="text-sm hover:text-cream-50 transition-colors">Politica de confidentialitate</Link></li>
              <li><Link to="/cookies" className="text-sm hover:text-cream-50 transition-colors">Politica de cookies</Link></li>
              <li><Link to="/gdpr" className="text-sm hover:text-cream-50 transition-colors">GDPR</Link></li>
              <li><Link to="/cancel-policy" className="text-sm hover:text-cream-50 transition-colors">Politica de anulare</Link></li>
              <li>
                <button
                  type="button"
                  onClick={openSettings}
                  className="text-sm hover:text-cream-50 transition-colors text-left"
                >
                  Setări cookie-uri
                </button>
              </li>
            </ul>
          </div>

          {/* Contact — populated from /content/contact (admin-editable in
              /admin/content). Each row only renders when the matching
              setting has a value, so the footer never displays a fake
              phone or address. The whole column is hidden if every field
              is empty (rare — the seed populates email + city by default). */}
          {hasAnyContact && (
            <div>
              <h3 className="font-display text-cream-50 font-semibold mb-4">Contact</h3>
              <ul className="space-y-3">
                {contact.email && (
                  <li className="flex items-center gap-3 text-sm">
                    <FiMail className="text-primary-400 flex-shrink-0" />
                    <a href={`mailto:${contact.email}`} className="hover:text-cream-50 transition-colors">
                      {contact.email}
                    </a>
                  </li>
                )}
                {contact.phone && (
                  <li className="flex items-center gap-3 text-sm">
                    <FiPhone className="text-primary-400 flex-shrink-0" />
                    <a href={`tel:${contact.phone.replace(/\s+/g, '')}`} className="hover:text-cream-50 transition-colors">
                      {contact.phone}
                    </a>
                  </li>
                )}
                {addressLine && (
                  <li className="flex items-start gap-3 text-sm">
                    <FiMapPin className="text-primary-400 flex-shrink-0 mt-0.5" />
                    {addressLine}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* ANPC + SAL/SOL */}
        <div className="mt-12 pt-8 border-t border-cream-50/15">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <a
              href="https://anpc.ro/ce-este-sal/"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              <img
                src="/anpc-sal.svg"
                alt="Solutionarea Alternativa a Litigiilor"
                width="250"
                height="40"
                loading="lazy"
                decoding="async"
                className="w-[200px] sm:w-[220px] md:w-[250px] h-auto"
              />
            </a>
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              <img
                src="/anpc-sol.svg"
                alt="Solutionarea Online a Litigiilor"
                width="250"
                height="40"
                loading="lazy"
                decoding="async"
                className="w-[200px] sm:w-[220px] md:w-[250px] h-auto"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-cream-50/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-cream-50/50 text-center sm:text-left">
              <p>&copy; {new Date().getFullYear()} Petzy. Toate drepturile rezervate.</p>
              {(company.name || company.cui) && (
                <p className="mt-1 text-cream-50/50 text-xs">
                  {company.name}{company.cui ? ` | CUI: ${company.cui}` : ''}{company.reg ? ` | ${company.reg}` : ''}{company.address ? ` | ${company.address}` : ''}
                </p>
              )}
            </div>
            <p className="text-sm text-cream-50/70">
              Făcut cu ❤️ în România
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
